import { inject } from '@angular/core';
import { Router, ActivatedRouteSnapshot, RouterStateSnapshot, UrlTree, CanActivateFn } from '@angular/router';
import { createAuthGuard, AuthGuardData } from 'keycloak-angular';

import { hasAdminRole } from './admin-roles';

const isAuthenticated = async (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
  authData: AuthGuardData
): Promise<boolean | UrlTree> => {
  const { authenticated, grantedRoles, keycloak } = authData;
  const router = inject(Router);

  if (!authenticated) {
    await keycloak.login({ redirectUri: window.location.origin + state.url });
    return false;
  }

  // Allow /forbidden through unconditionally so the redirect target below
  // does not loop on itself when the user lacks systray access.
  if (state.url.startsWith('/forbidden')) {
    return true;
  }

  // Cover3D SSO is realm-wide — cover-systray, cover-metalsheet, drawer2d-
  // service, mobile-backend etc. all live in the same Keycloak realm and
  // share the user pool. A user authenticated as e.g. cover3@cover3d.com
  // (a metalsheet QA account) therefore reaches this app even though they
  // have no business here, and the API 500s on the first call because the
  // tenant filter cannot resolve a tenant_id. Until the realm grows a
  // per-client access role / required attribute (ticket 5134 thread), gate
  // the app on the user-level tenant_id claim and bounce anyone without
  // one to /forbidden. Admins keep their bypass via the realm `admin` role
  // so a newly-promoted super-admin without the attribute is not locked
  // out of their own tooling.
  const tenantId = (keycloak.tokenParsed as Record<string, unknown> | undefined)?.['tenant_id'];
  const hasTenant = typeof tenantId === 'string' && tenantId.length > 0;
  const isAdmin = hasAdminRole(grantedRoles.realmRoles, ...Object.values(grantedRoles.resourceRoles));

  if (!hasTenant && !isAdmin) {
    return router.parseUrl('/forbidden');
  }

  return true;
};

const hasRequiredRoles = async (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
  authData: AuthGuardData
): Promise<boolean | UrlTree> => {
  const { authenticated, grantedRoles, keycloak } = authData;
  const router = inject(Router);

  if (!authenticated) {
    await keycloak.login({ redirectUri: window.location.origin + state.url });
    return false;
  }

  const requiredRoles: string[] = route.data['roles'] ?? [];

  if (requiredRoles.length === 0) {
    return true;
  }

  const hasRealmRole = requiredRoles.some(role =>
    grantedRoles.realmRoles.includes(role)
  );

  const hasResourceRole = requiredRoles.some(role =>
    Object.values(grantedRoles.resourceRoles)
      .some(roles => roles.includes(role))
  );

  if (hasRealmRole || hasResourceRole) {
    return true;
  }

  return router.parseUrl('/forbidden');
};

const isAdmin = async (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
  authData: AuthGuardData
): Promise<boolean | UrlTree> => {
  const { authenticated, grantedRoles, keycloak } = authData;
  const router = inject(Router);

  if (!authenticated) {
    await keycloak.login({ redirectUri: window.location.origin + state.url });
    return false;
  }

  const isAdminUser = hasAdminRole(grantedRoles.realmRoles, ...Object.values(grantedRoles.resourceRoles));

  return isAdminUser ? true : router.parseUrl('/');
};

// M2 Phase 11 Plan 5 (UI-M2-07): canAccessNonTryba / canActivateNonTryba removed.
// The route-level guard was never wired (0 references in app routes at audit
// time) and the role-based "non-Tryba" predicate is moot under the tenant-id
// scoping that the API now enforces via the EF global query filter. If a
// route ever needs to be restricted to a specific tenant in the future, write
// a `canActivateForTenant(slug)` factory that reads
// `userService.tenantId()` rather than re-introducing a role-based predicate.

export const canActivateAuth = createAuthGuard<CanActivateFn>(isAuthenticated);
export const canActivateWithRoles = createAuthGuard<CanActivateFn>(hasRequiredRoles);
export const canActivateAdmin = createAuthGuard<CanActivateFn>(isAdmin);
